import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { API_URL } from '../../config';
import { tokens } from '../../utils/theme';
import { BtnIcon } from '../../components/SharedPageStyles';
import { copyTableAsJSON } from '../../utils/clipboardUtils';
import { useMarketOverviewData } from '../../context/MarketOverviewContext';
import { getAdvancedEventSignal, getThreshold } from '../../utils/divergenceAlgorithm';
import { calculateRiskScore, calculateCycleScore } from '../../utils/batteryAlgorithms';
const Container = styled.div`
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'radial-gradient(circle at top left, rgba(130, 170, 255, 0.05), rgba(12, 14, 23, 0.95))'
        : 'radial-gradient(circle at top left, rgba(15,118,110,0.04), rgba(255,255,255,0.95))'};
    border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
    border-radius: 14px;
    padding: 20px 20px 0 20px; /* Removed bottom padding as scroll container handles it */
    color: ${props => props.theme.palette.text.primary};
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 10px;
    padding-bottom: 20px;
    margin-right: -10px; /* Offset padding for scrollbar positioning */

    /* Styled native scrollbar matching Fundamentals' custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.22);
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.08);
    }
    &::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.26), rgba(128, 203, 196, 0.44));
        border-radius: 999px;
        border: 1px solid rgba(128, 203, 196, 0.20);
    }
    &::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.4), rgba(128, 203, 196, 0.6));
    }
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
`;

const GaugeWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
`;

const GaugeText = styled.div`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${props => props.color};
`;

const MacroCompass = ({ score }) => {
    let level = 3;
    let color = '#78909c';
    if (score >= 3) { level = 5; color = '#4db6ac'; }
    else if (score > 0) { level = 4; color = '#80cbc4'; }
    else if (score <= -3) { level = 1; color = '#e57373'; }
    else if (score < 0) { level = 2; color = '#ef9a9a'; }

    return (
        <GaugeWrapper>
            <GaugeText color="rgba(255,255,255,0.7)" style={{ fontSize: '11px', marginBottom: '4px', letterSpacing: '0.1em' }}>
                RISK
            </GaugeText>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '10px', fontWeight: '600', color: 'rgba(255,255,255,0.5)' }}>OFF</div>

                <svg width="60" height="24" viewBox="0 0 60 24" style={{ overflow: 'visible' }}>
                    {/* Battery Body Outline */}
                    <rect x="1" y="1" width="54" height="22" rx="3" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                    {/* Battery Nub */}
                    <path d="M 55 8 L 58 8 A 2 2 0 0 1 60 10 L 60 14 A 2 2 0 0 1 58 16 L 55 16 Z" fill="rgba(255,255,255,0.3)" />

                    {/* Battery Segments */}
                    {[0, 1, 2, 3, 4].map(i => (
                        <rect
                            key={i}
                            x={4 + (i * 10)}
                            y="4"
                            width="8"
                            height="16"
                            rx="1"
                            fill={i < level ? color : "rgba(255,255,255,0.05)"}
                            style={{ transition: 'fill 0.4s ease' }}
                        />
                    ))}
                </svg>

                <div style={{ fontSize: '10px', fontWeight: '600', color: 'rgba(255,255,255,0.5)' }}>ON</div>
            </div>
        </GaugeWrapper>
    );
};

const SectorCompass = ({ score }) => {
    let level = 3;
    let color = '#78909c';
    if (score >= 4) { level = 5; color = '#4db6ac'; } // Strong Cyclical (Expansion)
    else if (score > 0) { level = 4; color = '#80cbc4'; } // Cyclical (Expansion)
    else if (score <= -4) { level = 1; color = '#e57373'; } // Strong Defensive (Contraction)
    else if (score < 0) { level = 2; color = '#ef9a9a'; } // Defensive (Contraction)

    return (
        <GaugeWrapper>
            <GaugeText color="rgba(255,255,255,0.7)" style={{ fontSize: '11px', marginBottom: '4px', letterSpacing: '0.1em' }}>
                CYCLE
            </GaugeText>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '10px', fontWeight: '600', color: 'rgba(255,255,255,0.5)' }}>REC</div>

                <svg width="60" height="24" viewBox="0 0 60 24" style={{ overflow: 'visible' }}>
                    <rect x="1" y="1" width="54" height="22" rx="3" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                    <path d="M 55 8 L 58 8 A 2 2 0 0 1 60 10 L 60 14 A 2 2 0 0 1 58 16 L 55 16 Z" fill="rgba(255,255,255,0.3)" />
                    {[0, 1, 2, 3, 4].map(i => (
                        <rect
                            key={i}
                            x={4 + (i * 10)}
                            y="4"
                            width="8"
                            height="16"
                            rx="1"
                            fill={i < level ? color : "rgba(255,255,255,0.05)"}
                            style={{ transition: 'fill 0.4s ease' }}
                        />
                    ))}
                </svg>

                <div style={{ fontSize: '10px', fontWeight: '600', color: 'rgba(255,255,255,0.5)' }}>EXP</div>
            </div>
        </GaugeWrapper>
    );
};

const ToggleGroup = styled.div`
    display: flex;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(15, 23, 42, 0.04)'};
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(15, 23, 42, 0.1)'};
`;

const ToggleBtn = styled.button`
    background: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.15)' : 'rgba(15,118,110,0.1)')
        : 'transparent'};
    color: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600])
        : tokens(props.theme.palette.mode).grey[400]};
    border: none;
    padding: 6px 12px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    font-weight: ${props => props.active ? '600' : '500'};
    
    &:hover {
        background: ${props => !props.active
        ? (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.05)')
        : (props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.25)' : 'rgba(15,118,110,0.15)')};
        color: ${props => props.active
        ? (props.theme.palette.mode === 'dark' ? '#b2dfdb' : '#115e59')
        : (props.theme.palette.mode === 'dark' ? '#fff' : '#000')};
    }
`;

const CategorySection = styled.div`
    margin-bottom: 30px;
`;

const CategoryTitle = styled.h4`
    font-size: 12px;
    font-weight: 600;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    margin-bottom: 15px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    font-size: 0.9rem;
    padding: 6px 8px;
    border-radius: 6px;
    border: 1px solid transparent;
    transition: all 0.2s ease;
    
    ${props => props.$highlight === 'pullback-buy' && `
        border-color: rgba(77, 182, 172, 0.2);
        background: rgba(77, 182, 172, 0.03);
    `}
    ${props => props.$highlight === 'relief-sell' && `
        border-color: rgba(229, 115, 115, 0.2);
        background: rgba(229, 115, 115, 0.03);
    `}
`;

const ping = keyframes`
  0% { transform: scale(0.8); opacity: 0.8; }
  80% { transform: scale(2.4); opacity: 0; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const PingIndicator = styled.div`
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 8px;
  background-color: ${props => props.$type === 'pullback-buy' ? '#4db6ac' : '#e57373'};

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    border: 1px solid ${props => props.$type === 'pullback-buy' ? '#4db6ac' : '#e57373'};
    animation: ${ping} 2s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
`;



const Label = styled.div`
    width: 150px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
`;

const Ticker = styled.span`
    font-weight: 500;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
`;

const AssetName = styled.span`
    font-size: 11px;
    color: ${props => props.theme.palette.mode === 'dark' ? '#90a4ae' : tokens(props.theme.palette.mode).grey[400]};
`;

const ChartArea = styled.div`
    flex-grow: 1;
    position: relative;
    height: 24px;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 4px;
    display: flex;
    align-items: center;
    margin: 0 15px;
`;

const CenterLine = styled.div`
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(255, 255, 255, 0.2);
    z-index: 1;
`;

const Bar = styled.div`
    position: absolute;
    height: 16px;
    top: 4px;
    border-radius: 2px;
    z-index: 2;
    transition: width 0.3s ease, left 0.3s ease, right 0.3s ease;
    
    ${props => props.isPositive ? `
        left: 50%;
        background-color: rgba(38, 166, 154, 0.8);
    ` : `
        right: 50%;
        background-color: rgba(239, 83, 80, 0.8);
    `}
`;

const ValueLabel = styled.div`
    width: 60px;
    text-align: right;
    font-weight: 500;
    color: ${props => props.isPositive
        ? tokens(props.theme.palette.mode).greenAccent[500]
        : (props.isNegative ? tokens(props.theme.palette.mode).redAccent[500] : tokens(props.theme.palette.mode).grey[400])};
`;

const Loading = styled.div`
    padding: 40px;
    text-align: center;
    color: rgba(255,255,255,0.5);
`;

const ErrorMsg = styled.div`
    padding: 20px;
    color: #ef5350;
    background: rgba(239, 83, 80, 0.1);
    border-radius: 4px;
    border: 1px solid rgba(239, 83, 80, 0.3);
`;

const PERIODS = [
    { key: 'D', label: 'D' },
    { key: 'W', label: 'W' },
    { key: 'M', label: 'M' },
    { key: 'MTD', label: 'MTD' },
    { key: 'Q', label: 'Q' },
    { key: 'HY', label: '6M' },
    { key: 'Y', label: 'Y' },
    { key: 'YTD', label: 'YTD' }
];


const IntermarketChart = ({ mode = "cross-assets" }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activePeriod, setActivePeriod] = useState('W');
    const { rows: overviewRows } = useMarketOverviewData();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${API_URL}/cross-asset/performance`);
                setData(res.data);
                setError(null);
            } catch (err) {
                console.error("Failed to fetch cross asset performance:", err);
                setError("Failed to load performance data. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) return <Container><Loading>Fetching latest market data...</Loading></Container>;
    if (error) return <Container><ErrorMsg>{error}</ErrorMsg></Container>;
    if (!data || data.length === 0) return null;

    // Filter categories based on mode
    const displayData = data.filter(category => {
        if (mode === "cross-sectors") {
            return category.category === "US Sectors";
        } else {
            return category.category !== "US Sectors";
        }
    });

    // Find absolute maximum value for the active period to scale the bars globally or per category
    // Let's scale globally so everything is relative to the biggest mover
    let maxAbsValue = 0.01; // Avoid div by zero
    displayData.forEach(category => {
        category.assets.forEach(asset => {
            const val = asset.performance[activePeriod] || 0;
            if (Math.abs(val) > maxAbsValue) {
                maxAbsValue = Math.abs(val);
            }
        });
    });

    // Calculate Market Regime Compass
    const riskScore = calculateRiskScore(data, activePeriod);
    const cycleScore = calculateCycleScore(data, activePeriod);

    const handleCopyJSON = () => {
        const formattedData = [];
        displayData.forEach(category => {
            category.assets.forEach(asset => {
                formattedData.push({
                    category: category.category,
                    ticker: asset.ticker,
                    name: asset.name,
                    performance: asset.performance[activePeriod] || 0,
                    timeframe: activePeriod
                });
            });
        });
        copyTableAsJSON(formattedData);
    };

    return (
        <Container>
            {mode === "cross-assets" && (
                <HeaderRow>
                    <div style={{ flex: 1 }}></div>

                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', paddingLeft: '92px' }}>
                        <MacroCompass score={riskScore} />
                    </div>

                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
                        <ToggleGroup>
                            {PERIODS.map(p => (
                                <ToggleBtn
                                    key={p.key}
                                    active={activePeriod === p.key}
                                    onClick={() => setActivePeriod(p.key)}
                                >
                                    {p.label}
                                </ToggleBtn>
                            ))}
                        </ToggleGroup>
                        <BtnIcon onClick={handleCopyJSON} title="Copy JSON">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
                                <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                        </BtnIcon>
                    </div>
                </HeaderRow>
            )}

            {mode === "cross-sectors" && (
                <HeaderRow>
                    <div style={{ flex: 1 }}></div>

                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', paddingLeft: '92px' }}>
                        <SectorCompass score={cycleScore} />
                    </div>

                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
                        <ToggleGroup>
                            {PERIODS.map(p => (
                                <ToggleBtn
                                    key={p.key}
                                    active={activePeriod === p.key}
                                    onClick={() => setActivePeriod(p.key)}
                                >
                                    {p.label}
                                </ToggleBtn>
                            ))}
                        </ToggleGroup>
                        <BtnIcon onClick={handleCopyJSON} title="Copy JSON">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
                                <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                        </BtnIcon>
                    </div>
                </HeaderRow>
            )}

            <ScrollableContent>
                {displayData.map(category => (
                    <CategorySection key={category.category}>
                        <CategoryTitle>{category.category}</CategoryTitle>
                        {category.assets.map(asset => {
                            const val = asset.performance[activePeriod] || 0;
                            const isPositive = val > 0;
                            const isNegative = val < 0;
                            // Bar width is percentage of 50% half-width
                            const widthPct = (Math.abs(val) / maxAbsValue) * 50;

                            const signal = getAdvancedEventSignal(asset.ticker, overviewRows, activePeriod);
                            const threshold = getThreshold(activePeriod);
                            
                            let highlightMode = null;
                            if (Math.abs(val) >= threshold) {
                                if (signal === 'BUY' && isNegative) {
                                    highlightMode = 'pullback-buy';
                                } else if (signal === 'SELL' && isPositive) {
                                    highlightMode = 'relief-sell';
                                }
                            }

                            return (
                                <Row key={asset.ticker} $highlight={highlightMode}>
                                    <Label>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Ticker>{asset.ticker}</Ticker>
                                            {highlightMode && (
                                                <PingIndicator 
                                                    $type={highlightMode} 
                                                    title={highlightMode === 'pullback-buy' ? 'DIP' : 'RALLY'}
                                                />
                                            )}
                                        </div>
                                        <AssetName>{asset.name}</AssetName>
                                    </Label>
                                    <ChartArea>
                                        <CenterLine />
                                        <Bar
                                            isPositive={isPositive}
                                            style={{ width: `${widthPct}%` }}
                                        />
                                    </ChartArea>
                                    <ValueLabel isPositive={isPositive} isNegative={isNegative}>
                                        {val > 0 ? '+' : ''}{val.toFixed(2)}%
                                    </ValueLabel>
                                </Row>
                            );
                        })}
                    </CategorySection>
                ))}
            </ScrollableContent>
        </Container>
    );
};

export default IntermarketChart;
